/**
 * React Hook for Audit Logging
 * Provides easy integration of audit logging into React components
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  auditLog,
  queryAuditLogs,
  getAuditStats,
  type AuditLogQuery,
  type AuditLogRecord,
  type AuditAction,
  type AuditStatus,
} from '@/lib/audit';

// =============================================================================
// AUDIT LOG WRITER HOOK
// =============================================================================

export function useAuditLog() {
  // Note: User context is handled server-side via auth.uid() in RLS policies

  const logAuth = useCallback(
    async (
      action: 'login' | 'logout' | 'login_failed' | 'password_change' | 'mfa_enroll' | 'mfa_verify',
      options: {
        status?: AuditStatus;
        failureReason?: string;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      await auditLog.logAuth(action, options);
    },
    []
  );

  const logDataAccess = useCallback(
    async (
      action: 'read' | 'export' | 'download',
      resourceType: string,
      resourceId: string,
      resourceName?: string,
      metadata?: Record<string, unknown>
    ) => {
      await auditLog.logDataAccess(action, resourceType, resourceId, resourceName, metadata);
    },
    []
  );

  const logDataChange = useCallback(
    async (
      action: 'create' | 'update' | 'delete',
      resourceType: string,
      resourceId: string,
      options: {
        resourceName?: string;
        oldValues?: Record<string, unknown>;
        newValues?: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      await auditLog.logDataChange(action, resourceType, resourceId, options);
    },
    []
  );

  const logPermissionChange = useCallback(
    async (
      action: 'role_assign' | 'role_revoke' | 'permission_change',
      targetUserId: string,
      targetUserEmail: string,
      options: {
        oldRole?: string;
        newRole?: string;
        scope?: 'org' | 'family';
        familyId?: string;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      await auditLog.logPermissionChange(action, targetUserId, targetUserEmail, options);
    },
    []
  );

  const logIntegration = useCallback(
    async (
      action: 'integration_connect' | 'integration_disconnect',
      integrationType: string,
      integrationId: string,
      integrationName: string,
      status: AuditStatus = 'success',
      failureReason?: string
    ) => {
      await auditLog.logIntegration(
        action,
        integrationType,
        integrationId,
        integrationName,
        status,
        failureReason
      );
    },
    []
  );

  const logAdminAction = useCallback(
    async (
      action: 'settings_change' | 'invite_sent' | 'invite_accepted',
      resourceType: string,
      resourceId: string,
      options: {
        resourceName?: string;
        oldValues?: Record<string, unknown>;
        newValues?: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      await auditLog.logAdminAction(action, resourceType, resourceId, options);
    },
    []
  );

  const logAccessDenied = useCallback(
    async (
      resourceType: string,
      resourceId: string,
      reason: string,
      metadata?: Record<string, unknown>
    ) => {
      await auditLog.logAccessDenied(resourceType, resourceId, reason, metadata);
    },
    []
  );

  return {
    logAuth,
    logDataAccess,
    logDataChange,
    logPermissionChange,
    logIntegration,
    logAdminAction,
    logAccessDenied,
    // Raw log function for custom entries
    log: auditLog.log.bind(auditLog),
  };
}

// =============================================================================
// AUDIT LOG QUERY HOOKS
// =============================================================================

export function useAuditLogs(
  orgId: string | undefined,
  options: Omit<AuditLogQuery, 'orgId'> = {}
) {
  return useQuery({
    queryKey: ['audit-logs', orgId, options],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      return queryAuditLogs({ orgId, ...options });
    },
    enabled: !!orgId,
    staleTime: 30000, // 30 seconds
  });
}

export function useAuditStats(orgId: string | undefined, days: number = 30) {
  return useQuery({
    queryKey: ['audit-stats', orgId, days],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      return getAuditStats(orgId, days);
    },
    enabled: !!orgId,
    staleTime: 60000, // 1 minute
  });
}

// =============================================================================
// HOOK FOR AUTOMATIC LOGGING ON DATA MUTATIONS
// =============================================================================

interface UseMutationAuditOptions {
  resourceType: string;
  getResourceName?: (data: unknown) => string;
  getResourceId?: (data: unknown) => string;
}

/**
 * Hook to wrap mutation callbacks with audit logging
 */
export function useMutationAudit(options: UseMutationAuditOptions) {
  const { logDataChange } = useAuditLog();

  const wrapCreate = useCallback(
    <T extends { id?: string }>(
      onSuccess: (data: T) => void
    ): ((data: T) => void) => {
      return (data: T) => {
        const resourceId = options.getResourceId?.(data) || data.id || 'unknown';
        const resourceName = options.getResourceName?.(data);
        logDataChange('create', options.resourceType, resourceId, {
          resourceName,
          newValues: data as Record<string, unknown>,
        });
        onSuccess(data);
      };
    },
    [logDataChange, options]
  );

  const wrapUpdate = useCallback(
    <T extends { id?: string }>(
      onSuccess: (data: T, variables: { oldData?: T }) => void
    ): ((data: T, variables: { oldData?: T }) => void) => {
      return (data: T, variables: { oldData?: T }) => {
        const resourceId = options.getResourceId?.(data) || data.id || 'unknown';
        const resourceName = options.getResourceName?.(data);
        logDataChange('update', options.resourceType, resourceId, {
          resourceName,
          oldValues: variables.oldData as Record<string, unknown>,
          newValues: data as Record<string, unknown>,
        });
        onSuccess(data, variables);
      };
    },
    [logDataChange, options]
  );

  const wrapDelete = useCallback(
    <T extends { id?: string }>(
      onSuccess: (data: T) => void
    ): ((data: T) => void) => {
      return (data: T) => {
        const resourceId = options.getResourceId?.(data) || data.id || 'unknown';
        const resourceName = options.getResourceName?.(data);
        logDataChange('delete', options.resourceType, resourceId, {
          resourceName,
          oldValues: data as Record<string, unknown>,
        });
        onSuccess(data);
      };
    },
    [logDataChange, options]
  );

  return {
    wrapCreate,
    wrapUpdate,
    wrapDelete,
  };
}
